import React from 'react';
import _ from 'lodash';
import { Collapse } from '@blueprintjs/core';

import { IRosterShift, IRosterWorker, IRosterWorkerWithShifts } from '../../types';
import { ROSTER_DAILY_CONFIG } from '../roster-daily-config';
import { Gridlines } from './Gridlines';
import { TimelineRow } from './TimelineRow';
import { WorkersTimelineHeader } from './WorkersTimelineHeader';
import { OpenShiftsList } from './open-shifts-list';
import { WorkerItemsList } from './worker-items-list';

const EMPTY_ROW_COUNT = 1; // Change this to show empty rows whenever there's no data. A bit buggy, so setting it to 1 for now.
const { TIMELINE_ROW_HEIGHT } = ROSTER_DAILY_CONFIG;

interface WorkersTimelineProps {
  hours: string[];
  assignedShifts: IRosterWorkerWithShifts[];
  unassignedShifts: IRosterShift[][];
  workers: IRosterWorker[];
  isWorkersOpen: boolean;
  refreshData?: () => void;
}

export function WorkersTimeline({
  hours,
  assignedShifts = [],
  unassignedShifts = [],
  workers = [],
  isWorkersOpen,
  refreshData,
}: WorkersTimelineProps) {
  const { HOUR_BLOCK_WIDTH } = ROSTER_DAILY_CONFIG;
  const rowWidth = _.size(hours) * HOUR_BLOCK_WIDTH;
  const hasOpenShifts = !_.isEmpty(unassignedShifts);
  const workersCount = _.size(workers);
  const hasWorkers = workersCount > 0;
  const openShiftsHeight = _.size(unassignedShifts) * TIMELINE_ROW_HEIGHT;
  const unassignedRowCount = _.size(unassignedShifts);
  const assignedRowCount = _.reduce(assignedShifts, (acc, shiftGroup) => acc + _.size(shiftGroup.shifts), 0);
  const totalRowCount = unassignedRowCount + assignedRowCount;

  return (
    <div>
      <WorkersTimelineHeader rowWidth={rowWidth} />
      <Collapse isOpen={isWorkersOpen} keepChildrenMounted={false}>
        <div className='flex-column position-relative'>
          <Gridlines hours={hours} rowCount={totalRowCount} />
          {!hasWorkers &&
            !hasOpenShifts &&
            _.times(EMPTY_ROW_COUNT, (idx) => (
              <TimelineRow rowWidth={rowWidth} key={idx} backgroundColor={'tertiary'} />
            ))}
          {hasOpenShifts && (
            <TimelineRow
              rowWidth={rowWidth}
              items={<OpenShiftsList unassignedShifts={unassignedShifts} />}
              rowHeight={openShiftsHeight}
            />
          )}
          {workers.map((worker, idx) => {
            const workerShifts = _.find(assignedShifts, (shifts) => shifts.supportWorkerId === worker.supportWorkerId);
            const shiftGroups = workerShifts && !_.isEmpty(workerShifts.shifts) ? workerShifts.shifts : [];
            const rowHeight = _.size(shiftGroups) > 0 ? TIMELINE_ROW_HEIGHT * _.size(shiftGroups) : TIMELINE_ROW_HEIGHT;

            return (
              <TimelineRow
                rowWidth={rowWidth}
                // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                key={idx}
                rowHeight={rowHeight}
                items={<WorkerItemsList shifts={shiftGroups} refreshData={refreshData} />}
              />
            );
          })}
        </div>
      </Collapse>
    </div>
  );
}
